'use client';

import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { getApiClient } from 'saletrax-shared/clients/apiClient';
import { useAuth } from './authContext';

export interface CartItem {
  itemId: string;
  name: string;
  price: number;
  unit: string;
  quantity: number;
  image?: string;
  priceAtAdd: number;
  variation?: string;
}

interface CartContextType {
  items: CartItem[];
  storeId: string | null;
  addItem: (item: Omit<CartItem, 'quantity' | 'priceAtAdd'> & { quantity?: number }) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number, variation?: string) => void;
  clearCart: () => void;
  itemCount: number;
  subtotal: number;
  isLoading: boolean;
}

const CartContext = createContext<CartContextType | null>(null);

export const CartProvider = ({
  children,
  storeId
}: {
  children: ReactNode;
  storeId: string;
}) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const prevUserRef = useRef<string | null>(null);

  const api = getApiClient();

  // Load cart on mount or when user changes
  useEffect(() => {
    const loadCart = async () => {
      setIsLoading(true);

      if (user) {
        try {
          // If user just logged in, merge guest cart
          if (!prevUserRef.current && user.id) {
            const guestCartKey = `cart-${storeId}`;
            const guestCartData = localStorage.getItem(guestCartKey);

            if (guestCartData) {
              const guestItems: CartItem[] = JSON.parse(guestCartData);
              if (guestItems.length > 0) {
                // Load existing user cart from backend
                let existingItems: CartItem[] = [];
                try {
                  const res = await api.get(`/auth/marketplace/cart?storefrontId=${storeId}`);
                  const cart = res.data.data;
                  if (cart?.items && cart.storefrontId === storeId) {
                    existingItems = cart.items;
                  }
                } catch {
                  // No existing cart
                }

                // Merge guest items with existing
                const mergedItems = [...guestItems];
                existingItems.forEach(existingItem => {
                  const guestIdx = mergedItems.findIndex(gi =>
                    gi.itemId === existingItem.itemId && gi.variation === existingItem.variation
                  );
                  if (guestIdx >= 0) {
                    mergedItems[guestIdx].quantity += existingItem.quantity;
                  } else {
                    mergedItems.push(existingItem);
                  }
                });

                // Save merged cart and clear localStorage
                await api.put('/auth/marketplace/cart', { storefrontId: storeId, items: mergedItems });
                localStorage.removeItem(guestCartKey);
                setItems(mergedItems);
                setIsLoading(false);
                prevUserRef.current = user.id;
                return;
              }
            }
          }

          // Load user cart from backend
          const res = await api.get(`/auth/marketplace/cart?storefrontId=${storeId}`);
          const cart = res.data.data;
          if (cart?.items && (cart.storefrontId === storeId || !cart.storefrontId)) {
            setItems(cart.items || []);
          } else {
            setItems([]);
          }
        } catch {
          setItems([]);
        }
      } else {
        // Load from localStorage for guests
        const localCart = localStorage.getItem(`cart-${storeId}`);
        if (localCart) {
          try {
            setItems(JSON.parse(localCart));
          } catch {
            setItems([]);
          }
        }
      }

      prevUserRef.current = user?.id || null;
      setIsLoading(false);
    };

    loadCart();
  }, [user?.id, storeId]);

  // Save cart to backend or localStorage
  const saveCart = useCallback(async (newItems: CartItem[]) => {
    if (user) {
      const cleanedItems = newItems.map(item => ({
        itemId: item.itemId || '',
        name: item.name || '',
        price: item.price || 0,
        unit: item.unit || '',
        quantity: item.quantity || 1,
        priceAtAdd: item.priceAtAdd || item.price || 0,
        ...(item.image && { image: item.image }),
        ...(item.variation && { variation: item.variation }),
      }));

      try {
        await api.put('/auth/marketplace/cart', { storefrontId: storeId, items: cleanedItems });
      } catch {
        // Save failed — cart state still updated locally
      }
    } else {
      localStorage.setItem(`cart-${storeId}`, JSON.stringify(newItems));
    }
  }, [user, storeId, api]);

  const addItem = useCallback((item: Omit<CartItem, 'quantity' | 'priceAtAdd'> & { quantity?: number; variation?: string }) => {
    setItems((prev) => {
      const existingIndex = prev.findIndex((i) =>
        i.itemId === item.itemId &&
        (item.variation ? i.variation === item.variation : !i.variation)
      );
      let newItems: CartItem[];

      if (existingIndex >= 0) {
        newItems = prev.map((i, idx) =>
          idx === existingIndex
            ? { ...i, quantity: i.quantity + (item.quantity || 1) }
            : i
        );
      } else {
        newItems = [...prev, {
          itemId: item.itemId || '',
          name: item.name || '',
          price: item.price || 0,
          unit: item.unit || '',
          quantity: item.quantity || 1,
          priceAtAdd: item.price || 0,
          ...(item.image && { image: item.image }),
          ...(item.variation && { variation: item.variation }),
        }];
      }

      saveCart(newItems);
      return newItems;
    });
  }, [saveCart]);

  const removeItem = useCallback((itemId: string) => {
    setItems((prev) => {
      const newItems = prev.filter((i) => i.itemId !== itemId);
      saveCart(newItems);
      return newItems;
    });
  }, [saveCart]);

  const updateQuantity = useCallback((itemId: string, quantity: number, variation?: string) => {
    if (quantity <= 0) {
      if (variation !== undefined) {
        setItems((prev) => {
          const newItems = prev.filter((i) =>
            !(i.itemId === itemId && i.variation === variation)
          );
          saveCart(newItems);
          return newItems;
        });
      } else {
        removeItem(itemId);
      }
      return;
    }

    setItems((prev) => {
      const newItems = prev.map((i) => {
        if (variation !== undefined) {
          return (i.itemId === itemId && i.variation === variation)
            ? { ...i, quantity }
            : i;
        } else {
          return (i.itemId === itemId && !i.variation)
            ? { ...i, quantity }
            : i;
        }
      });
      saveCart(newItems);
      return newItems;
    });
  }, [saveCart, removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
    saveCart([]);
  }, [saveCart]);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  return (
    <CartContext.Provider value={{
      items,
      storeId,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      itemCount,
      subtotal,
      isLoading,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
